import { toError } from '@fruitful/fruitils';
import { type GroupChannel } from '@sendbird/chat/groupChannel';
import {
    type BaseMessage,
    type FileMessage,
    MessageType,
    MessageTypeFilter,
} from '@sendbird/chat/message';
import { useEffect, useState } from 'react';

import { captureException } from '../../logger/Logger';

const MAX_FILE_MESSAGES = 500;
const PAGE_SIZE = 100;

interface UseGetChannelFileMessagesResult {
    fileMessages: FileMessage[];
    isLoading: boolean;
    hasLoaded: boolean;
    error: Error | null;
}

const isFileMessage = (message: BaseMessage): message is FileMessage =>
    message.messageType === MessageType.FILE &&
    'url' in message &&
    'name' in message;

// Fetches all file messages from a Sendbird channel.
export const useGetChannelFileMessages = (
    channel: GroupChannel | null,
): UseGetChannelFileMessagesResult => {
    const [fileMessages, setFileMessages] = useState<FileMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (channel == null) {
            setFileMessages([]);
            setIsLoading(false);
            setHasLoaded(false);
            setError(null);
            return;
        }

        let isCancelled = false;

        setFileMessages([]);
        setIsLoading(true);
        setHasLoaded(false);
        setError(null);

        const fetchFileMessages = async () => {
            try {
                const query = channel.createPreviousMessageListQuery({
                    limit: PAGE_SIZE,
                    reverse: true,
                    messageTypeFilter: MessageTypeFilter.FILE,
                });

                const messages: FileMessage[] = [];
                const seenMessageIds = new Set<number>();

                while (query.hasNext && messages.length < MAX_FILE_MESSAGES) {
                    const result = await query.load();

                    if (isCancelled) {
                        return;
                    }

                    if (result.length === 0) {
                        break;
                    }

                    for (const message of result) {
                        if (
                            isFileMessage(message) &&
                            !seenMessageIds.has(message.messageId)
                        ) {
                            seenMessageIds.add(message.messageId);
                            messages.push(message);
                        }
                    }
                }

                if (!isCancelled) {
                    messages.sort((a, b) => b.createdAt - a.createdAt);
                    setFileMessages(messages);
                    setHasLoaded(true);
                    setIsLoading(false);
                }
            } catch (err) {
                const fetchError = toError(err);
                captureException(fetchError);
                if (!isCancelled) {
                    setFileMessages([]);
                    setError(fetchError);
                    setHasLoaded(true);
                    setIsLoading(false);
                }
            }
        };

        void fetchFileMessages();

        return () => {
            isCancelled = true;
        };
    }, [channel]);

    return {
        fileMessages,
        isLoading,
        hasLoaded,
        error,
    };
};
